import Avatar from '@mui/material/Avatar';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';
import { fToNow, fDate } from 'src/utils/format-time';
import { TenantType } from 'src/types/enums';
import { ICompanyListItem } from 'src/services/connections/connections.types';

import { ColumnDef } from 'src/components/data-list/data-list.types';

// ----------------------------------------------------------------------

type Translate = (key: string, options?: any) => string;

const dash = <Typography variant="body2" color="textSecondary">--</Typography>;

/**
 * Companies list column registry (JT1-1528, item 27). Logo + name (links to detail), city, number
 * of jobs, number of contacts, owner, last activity. Counts come from the lean endpoint's grouped
 * aggregates; the LinkedIn icon (item 29 / JT1-1480) is a follow-up.
 */
export function buildCompanyColumns({ t, tenantType }: { t: Translate; tenantType?: TenantType }): ColumnDef<ICompanyListItem>[] {
  const detailPath = (row: ICompanyListItem) =>
    paths.dashboard.companies.detail(row.id, tenantType as TenantType, (row.company_type ?? tenantType) as TenantType);

  return [
    {
      key: 'name',
      label: t('companies.table.name', { defaultValue: 'Company' }),
      fixed: true,
      sortable: true,
      searchable: true,
      minWidth: 220,
      render: (row) => (
        <Stack direction="row" alignItems="center" gap={1}>
          <Avatar src={row.logo_url ?? undefined} alt={row.company_name} sx={{ width: 28, height: 28, fontSize: 13 }}>
            {row.company_name?.charAt(0)?.toUpperCase()}
          </Avatar>
          <Link component={RouterLink} href={detailPath(row)} color="inherit" noWrap>
            <Typography variant="subtitle2">{row.company_name || '--'}</Typography>
          </Link>
        </Stack>
      ),
    },
    {
      key: 'city',
      label: t('companies.table.city', { defaultValue: 'Location' }),
      defaultVisible: true,
      sortable: true,
      searchable: true,
      render: (row) => (row.city ? <Typography variant="body2" color="text.secondary">{row.city}</Typography> : dash),
    },
    {
      key: 'jobs',
      label: t('companies.table.jobs', { defaultValue: 'Jobs' }),
      defaultVisible: true,
      align: 'right',
      render: (row) => <Typography variant="body2" color="text.secondary">{row.job_count}</Typography>,
    },
    {
      key: 'contacts',
      label: t('companies.table.contacts', { defaultValue: 'Contacts' }),
      defaultVisible: true,
      align: 'right',
      render: (row) => <Typography variant="body2" color="text.secondary">{row.contact_count}</Typography>,
    },
    {
      key: 'account_owner',
      label: t('companies.table.owner', { defaultValue: 'Owner' }),
      defaultVisible: false,
      render: (row) => (row.account_owner_name ? <Typography variant="body2" color="text.secondary">{row.account_owner_name}</Typography> : dash),
    },
    {
      key: 'last_activity_at',
      label: t('companies.table.last_updated', { defaultValue: 'Last updated' }),
      defaultVisible: true,
      render: (row) =>
        row.last_activity_at ? (
          <Tooltip title={fDate(row.last_activity_at)}>
            <Typography variant="body2" color="text.secondary">{fToNow(row.last_activity_at)}</Typography>
          </Tooltip>
        ) : dash,
    },
  ];
}
